// src/hooks/useArrivals.ts
//
// Data hook for «Llegadas de hoy» (mockup 01) and the wall screen (mockup
// 02): polls `GET /api/v1/ui/arrivals` every 15 s, tracks the "Actualizado
// hace N s" counter, and exposes acknowledge/pin actions with an optimistic
// local update so a click reflects immediately while the next poll confirms it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataProvider } from 'react-admin';
import type { ArrivalsResponse, ReservationState } from '@/types/reservationState';

const POLL_MS = 15_000;

export interface UseArrivalsOptions {
  /** An explicit calendar date; without it the server uses each establishment's business day. */
  date?: string;
  /** «Hoy» or «Mañana» relative to each establishment's business day (default: today). */
  day?: 'today' | 'tomorrow';
  pendingOnly?: boolean;
  hotelIds?: string;
  hotelId?: string;
  groupId?: string;
  /** Disable polling (e.g. while a drawer with its own refresh is open). */
  enabled?: boolean;
  /** Override the poll interval (the wall screen may want a different cadence). */
  pollMs?: number;
}

export interface UseArrivalsResult {
  data: ArrivalsResponse | null;
  loading: boolean;
  error: boolean;
  consecutiveFailures: number;
  secondsSinceUpdate: number;
  refresh: () => void;
  acknowledge: (id: string) => Promise<void>;
  pin: (id: string, pinned: boolean) => Promise<void>;
}

const patchReservation = (
  response: ArrivalsResponse,
  id: string,
  patch: Partial<ReservationState['attention']>
): ArrivalsResponse => {
  const apply = (row: ReservationState): ReservationState =>
    row.id === id ? { ...row, attention: { ...row.attention, ...patch } } : row;
  return {
    ...response,
    arrivals: response.arrivals.map(apply),
    live: response.live.map((group) => ({ ...group, sessions: group.sessions.map(apply) })),
  };
};

export const useArrivals = (options: UseArrivalsOptions = {}): UseArrivalsResult => {
  const { date, day, pendingOnly, hotelIds, hotelId, groupId, enabled = true, pollMs = POLL_MS } = options;
  const dataProvider = useDataProvider();

  const [data, setData] = useState<ArrivalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(null);
  const [secondsSinceUpdate, setSecondsSinceUpdate] = useState(0);

  const abortRef = useRef<AbortController | null>(null);

  const fetchOnce = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const result = await dataProvider.getArrivals(
        { date, day, pendingOnly, hotelIds, hotelId, groupId },
        controller.signal
      );
      setData(result.data);
      setError(false);
      setConsecutiveFailures(0);
      setLastUpdatedAt(Date.now());
    } catch (err: any) {
      if (err?.name === 'AbortError') return;
      setError(true);
      setConsecutiveFailures((n) => n + 1);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProvider, date, day, pendingOnly, hotelIds, hotelId, groupId]);

  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    void fetchOnce();
    const handle = setInterval(() => void fetchOnce(), pollMs);
    return () => {
      clearInterval(handle);
      abortRef.current?.abort();
    };
  }, [enabled, pollMs, fetchOnce]);

  useEffect(() => {
    const tick = setInterval(() => {
      if (lastUpdatedAt) setSecondsSinceUpdate(Math.floor((Date.now() - lastUpdatedAt) / 1000));
    }, 1000);
    return () => clearInterval(tick);
  }, [lastUpdatedAt]);

  const acknowledge = useCallback(
    async (id: string) => {
      setData((prev) => (prev ? patchReservation(prev, id, { acknowledged: true }) : prev));
      try {
        await dataProvider.acknowledgeReservation(id, true);
      } catch {
        // The next poll reconciles state; an optimistic ack failing silently
        // is preferable to blocking the receptionist with a retry dialog.
      }
    },
    [dataProvider]
  );

  const pin = useCallback(
    async (id: string, pinned: boolean) => {
      setData((prev) => (prev ? patchReservation(prev, id, { pinned }) : prev));
      try {
        await dataProvider.pinReservation(id, pinned);
      } catch {
        // See acknowledge() above.
      }
    },
    [dataProvider]
  );

  return {
    data,
    loading,
    error,
    consecutiveFailures,
    secondsSinceUpdate,
    refresh: () => void fetchOnce(),
    acknowledge,
    pin,
  };
};

export default useArrivals;
